import { useEffect, useState } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../context/AuthContext";
import logger from "../utils/logger";
import { CurrencyInr, ChartLineUp, Cube, Receipt, ArrowCounterClockwise, Stack } from "@phosphor-icons/react";

const inr = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function SubscriptionFinancials() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    axios.get(`${API}/admin/subscription-financials`, { headers: getAuthHeaders() })
      .then((res) => setData(res.data))
      .catch((e) => logger.error("subscription-financials:", e))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  if (loading && !data) {
    return (
      <div className="rounded-md border border-white/10 p-5 h-40 flex items-center justify-center" style={{ background: "#121212" }}>
        <div className="w-5 h-5 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const { totals, by_tier, active_subscriptions_by_tier, constants } = data;
  const tierOrder = ["starter", "quarterly", "half_year", "annual"];

  return (
    <div className="rounded-md border border-white/10 p-5" style={{ background: "#121212" }} data-testid="subscription-financials">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-bold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Subscription Financials</h3>
          <p className="text-[10px] text-[#A1A1AA] mt-0.5">
            Real-time roll-up across {totals.order_count} verified orders ·
            company profit ₹{constants.company_profit_per_cycle_inr.toFixed(0)} per {constants.base_cycle_days}-day cycle · server = net revenue − profit
          </p>
        </div>
        <button onClick={refresh} data-testid="refresh-financials" className="p-2 rounded-md hover:bg-white/5 transition-colors">
          <ArrowCounterClockwise size={16} className="text-[#A1A1AA]" />
        </button>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KPI testid="kpi-gross-revenue" icon={CurrencyInr} color="#7DD3FC"
             label="Total Gross Revenue" value={inr(totals.total_gross_revenue)}
             sub="GST-inclusive across all orders" />
        <KPI testid="kpi-gst-outflow" icon={Receipt} color="#F43F5E"
             label={`GST Outflow (${constants.gst_rate_pct}%)`} value={inr(totals.total_gst_outflow)}
             sub="Owed to tax authority" />
        <KPI testid="kpi-server-cost" icon={Cube} color="#A855F7"
             label="Server Cost" value={inr(totals.total_server_cost_accrued)}
             sub="Net revenue − company profit" />
        <KPI testid="kpi-net-profit" icon={ChartLineUp} color="#10B981"
             label="Company Profit" value={inr(totals.pure_net_profit)}
             sub={`Flat ₹${constants.company_profit_per_cycle_inr.toFixed(0)} × (days/${constants.base_cycle_days})`} />
      </div>

      {/* Per-tier breakdown */}
      <div className="rounded-md border border-white/5 overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-white/10 bg-white/[0.02]">
              <th className="text-left px-3 py-2 text-[9px] font-bold uppercase tracking-[0.18em] text-[#A1A1AA]">Tier</th>
              <th className="text-right px-3 py-2 text-[9px] font-bold uppercase tracking-[0.18em] text-[#A1A1AA]">Orders</th>
              <th className="text-right px-3 py-2 text-[9px] font-bold uppercase tracking-[0.18em] text-[#A1A1AA]">Active</th>
              <th className="text-right px-3 py-2 text-[9px] font-bold uppercase tracking-[0.18em] text-[#A1A1AA]">Gross</th>
              <th className="text-right px-3 py-2 text-[9px] font-bold uppercase tracking-[0.18em] text-[#A1A1AA]">GST</th>
              <th className="text-right px-3 py-2 text-[9px] font-bold uppercase tracking-[0.18em] text-[#A1A1AA]">Net Rev</th>
              <th className="text-right px-3 py-2 text-[9px] font-bold uppercase tracking-[0.18em] text-[#A1A1AA]">Server</th>
              <th className="text-right px-3 py-2 text-[9px] font-bold uppercase tracking-[0.18em] text-[#A1A1AA]">Net Profit</th>
            </tr>
          </thead>
          <tbody>
            {tierOrder.map((code) => {
              const t = by_tier[code];
              if (!t) return null;
              return (
                <tr key={code} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`tier-row-${code}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Stack size={12} weight="duotone" className="text-[#00E5FF]" />
                      <div>
                        <p className="text-xs text-white">{t.label}</p>
                        <p className="text-[9px] text-[#737373]">{t.days}d · ₹{t.price_inr.toLocaleString("en-IN")}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right text-zinc-200 font-mono">{t.orders}</td>
                  <td className="px-3 py-2 text-right text-emerald-300 font-mono">{active_subscriptions_by_tier?.[code] ?? 0}</td>
                  <td className="px-3 py-2 text-right text-zinc-200 font-mono">{inr(t.gross_revenue)}</td>
                  <td className="px-3 py-2 text-right text-rose-300 font-mono">{inr(t.gst_outflow)}</td>
                  <td className="px-3 py-2 text-right text-zinc-200 font-mono">{inr(t.net_revenue)}</td>
                  <td className="px-3 py-2 text-right text-amber-300 font-mono">{inr(t.server_cost)}</td>
                  <td className="px-3 py-2 text-right text-emerald-300 font-mono font-bold">{inr(t.net_profit)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KPI({ testid, icon: Icon, color, label, value, sub }) {
  return (
    <div data-testid={testid} className="rounded-md border border-white/5 bg-white/[0.02] p-3">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon size={14} weight="duotone" style={{ color }} />
        <p className="text-[9px] font-bold uppercase tracking-wider" style={{ color }}>{label}</p>
      </div>
      <p className="text-lg font-black text-white font-mono" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{value}</p>
      <p className="text-[10px] text-[#737373] mt-0.5 leading-snug">{sub}</p>
    </div>
  );
}
